import { BaseTransformer } from './BaseTransformer';
import { TransportationForm } from '../schemas/forms/transportation';
import {
  CustomsDeclaration,
  TransportContractDocumentType,
  WeightUnit,
} from '__generated__/graphql';

type GoodsShipmentSource = Partial<CustomsDeclaration>;

export class TransportationTransformer extends BaseTransformer<
  GoodsShipmentSource,
  TransportationForm
> {
  toForm(source: GoodsShipmentSource): TransportationForm {
    const gs = source.goodsShipment?.[0] ?? {};
    const consignment = gs.consignment?.[0] ?? {};
    const btm = consignment.borderTransportationMean?.[0] ?? {};
    const docs = consignment.transportContractDocument ?? [];

    const masterDoc = docs.find((d) => d.type === 'MASTER_BILL_OF_LADING');
    const houseDoc = docs.find(
      (d) => d.type === 'HOUSE_BILL_OF_LADING' || d.type === 'HOUSE_WAYBILL',
    );

    return {
      masterBillOfLading: masterDoc?.identification ?? undefined,
      houseBillOfLading: houseDoc?.identification ?? undefined,
      quantityPcs: undefined,
      transportEquipment: (() => {
        const items = (consignment.transportEquipment ?? [])
          .filter((eq) => eq.identification || eq.name)
          .map((eq) => ({
            identification: eq.identification ?? undefined,
            description: eq.name ?? undefined,
            quantity: undefined,
            weight: eq.weight?.value ?? undefined,
            weightUnit: eq.weight?.unit ?? undefined,
          }));
        return items.length
          ? items
          : [
              {
                identification: undefined,
                description: undefined,
                quantity: undefined,
                weight: undefined,
                weightUnit: undefined,
              },
            ];
      })(),
      vesselName: btm.name ?? undefined,
      voyageNumber: btm.journey ?? undefined,
      vesselCountryFlag: undefined,
      containerized: consignment.containerized ?? undefined,
      grossWeight: undefined,
      portOfLading: btm.departureLocation?.[0]?.identification ?? undefined,
      exportCountry: gs.exportCountry?.[0]?.country ?? undefined,
      exportDate: btm.departure ?? undefined,
      portOfUnlading: btm.arrivalLocation?.[0]?.identification ?? undefined,
      estimatedArrivalDate: btm.arrival ?? undefined,
      destination: gs.importCountry?.[0]?.country ?? undefined,
    };
  }

  toSchema(
    formData: TransportationForm,
    original: GoodsShipmentSource,
  ): GoodsShipmentSource {
    const gs = original.goodsShipment?.[0] ?? {};
    const consignment = gs.consignment?.[0] ?? {};
    const btm = consignment.borderTransportationMean?.[0] ?? {};

    const docs: Array<{
      identification?: string;
      type?: TransportContractDocumentType;
    }> = [];
    if (formData.masterBillOfLading !== undefined) {
      docs.push({
        type: TransportContractDocumentType.MASTER_BILL_OF_LADING,
        identification: formData.masterBillOfLading,
      });
    }
    if (formData.houseBillOfLading !== undefined) {
      docs.push({
        type: TransportContractDocumentType.HOUSE_BILL_OF_LADING,
        identification: formData.houseBillOfLading,
      });
    }

    // Filter out docs with empty identification to avoid backend validation errors
    const validDocs = docs.filter((d) => d.identification);

    return {
      ...original,
      goodsShipment: [
        {
          ...gs,
          exportCountry:
            formData.exportCountry !== undefined
              ? [{ country: formData.exportCountry }]
              : gs.exportCountry,
          importCountry:
            formData.destination !== undefined
              ? [{ country: formData.destination }]
              : gs.importCountry,
          consignment: [
            {
              ...consignment,
              containerized: formData.containerized,
              transportContractDocument: validDocs.length
                ? validDocs
                : consignment.transportContractDocument,
              transportEquipment: formData.transportEquipment.map((eq) => ({
                identification: eq.identification,
                name: eq.description,
                // Echo the original unit; default to KILOGRAM for
                // new rows. Omit when weight is empty (backend rejects
                // a null WeightInput — value is non-null).
                weight:
                  eq.weight != null
                    ? {
                        value: eq.weight,
                        unit:
                          (eq.weightUnit as WeightUnit) ?? WeightUnit.KILOGRAM,
                      }
                    : undefined,
              })),
              borderTransportationMean: [
                {
                  ...btm,
                  name: formData.vesselName,
                  journey: formData.voyageNumber,
                  departure: formData.exportDate,
                  arrival: formData.estimatedArrivalDate,
                  departureLocation:
                    formData.portOfLading !== undefined
                      ? [{ identification: formData.portOfLading }]
                      : btm.departureLocation,
                  arrivalLocation:
                    formData.portOfUnlading !== undefined
                      ? [{ identification: formData.portOfUnlading }]
                      : btm.arrivalLocation,
                },
              ],
            },
          ],
        },
      ],
    };
  }
}
